/**
 * Truth Reset Exercise Page
 * 
 * A guided 5-step exercise to help members:
 * 1. Identify limiting beliefs
 * 2. Trace their origin
 * 3. Examine the evidence
 * 4. Craft a new truth
 * 5. Commit & embody the change
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useCelebration } from '@/context/CelebrationContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  ArrowLeft, ArrowRight, Loader2, Sparkles, Cloud, Search,
  Scale, Sunrise, Heart, CheckCircle, Trophy, RefreshCw,
  Quote, Eye, Calendar, ChevronRight, Star, Mic
} from 'lucide-react';
import VoiceInput from '@/components/voice/VoiceInput';

const API_URL = window.location.origin;

const STEP_ICONS = {
  cloud: Cloud,
  search: Search,
  scale: Scale,
  sunrise: Sunrise,
  heart: Heart
};

const TruthResetPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);
  const [steps, setSteps] = useState([]);
  const [currentExercise, setCurrentExercise] = useState(null);
  const [response, setResponse] = useState({});
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [activeTab, setActiveTab] = useState('current');
  const { celebrate, grandCelebrate, epicCelebrate } = useCelebration();

  // Step-specific celebration messages
  const STEP_CELEBRATIONS = {
    1: { message: "You named it. That takes courage.", type: "confetti" },
    2: { message: "You traced it back. Awareness is power.", type: "confetti" },
    3: { message: "You examined the evidence. Truth seeker!", type: "sparkle" },
    4: { message: "A NEW TRUTH IS BORN! This is YOUR declaration!", type: "grand" },
    5: { message: "You chose transformation. This is YOUR moment!", type: "epic" }
  };

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/truth-reset/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgress(response.data);
      setSteps(response.data.steps || []);
      setCurrentExercise(response.data.current_exercise);
      
      // Pre-fill responses if exercise in progress
      if (response.data.current_exercise?.responses) {
        const stepResponses = {};
        Object.entries(response.data.current_exercise.responses).forEach(([step, data]) => {
          // Handle nested content structure from API
          stepResponses[step] = data.content || data;
        });
        setResponse(stepResponses);
      }
    } catch (error) {
      console.error('Failed to fetch progress:', error);
      toast.error('Failed to load progress');
    } finally {
      setLoading(false);
    }
  };

  const startExercise = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const result = await axios.post(`${API_URL}/api/truth-reset/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentExercise(result.data.exercise);
      toast.success(result.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start exercise');
    } finally {
      setSaving(false);
    }
  };

  const saveStep = async () => {
    if (!currentExercise) return;
    
    const step = currentExercise.current_step;
    const stepResponse = response[step];
    
    if (!stepResponse || (typeof stepResponse === 'object' && !Object.values(stepResponse).some(v => v))) {
      toast.error('Please complete your response before continuing');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const result = await axios.post(
        `${API_URL}/api/truth-reset/${currentExercise.id}/step/${step}`,
        stepResponse,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Trigger celebration based on step
      const celebrationConfig = STEP_CELEBRATIONS[step];
      if (celebrationConfig) {
        if (celebrationConfig.type === 'epic') {
          // Skip step 5 celebration here - will be shown in the completion block below
        } else if (celebrationConfig.type === 'grand') {
          grandCelebrate('sparkle', 'milestone');
          // Show custom message after a brief delay
          setTimeout(() => {
            toast.success(celebrationConfig.message, { duration: 4000 });
          }, 500);
        } else if (celebrationConfig.type === 'sparkle') {
          grandCelebrate('sparkle');
        } else {
          celebrate('confetti', celebrationConfig.message);
        }
      }
      
      toast.success(result.data.message);
      
      if (result.data.completed) {
        // Exercise completed! - Show ONLY ONE epic celebration with varied messages
        const completionMessages = [
          "You've transformed a limiting belief into your NEW TRUTH! 💎👑",
          "This is your moment of transformation! You did it, Queen! 💜✨",
          "A new chapter begins NOW. Your truth has been reset! 🌅💎",
          "You've rewritten your story. This truth is YOURS! 👑🔥",
          "Breakthrough achieved! Watch how this new belief changes everything! 💫💜"
        ];
        const randomMessage = completionMessages[Math.floor(Math.random() * completionMessages.length)];
        epicCelebrate(randomMessage);
        setCurrentExercise(null);
        fetchProgress();
      } else {
        // Move to next step
        setCurrentExercise({
          ...currentExercise,
          current_step: step + 1,
          responses: {
            ...currentExercise.responses,
            [step]: { content: stepResponse }
          }
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save step');
    } finally {
      setSaving(false);
    }
  };

  const discardExercise = async () => {
    if (!window.confirm('Are you sure you want to discard this exercise? Your progress will be lost.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/truth-reset/${currentExercise.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentExercise(null);
      setResponse({});
      toast.success('Exercise discarded');
    } catch (error) {
      toast.error('Failed to discard exercise');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-terracotta" />
        </div>
      </DashboardLayout>
    );
  }

  const currentStep = currentExercise ? steps[currentExercise.current_step - 1] : null;
  const StepIcon = currentStep ? STEP_ICONS[currentStep.icon] || Sparkles : Sparkles;

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto" data-testid="truth-reset-page">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <RefreshCw className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-heading text-forest">Truth Reset Exercise</h1>
              <p className="text-gray-600">Transform limiting beliefs into empowering truths</p>
            </div>
          </div>
        </div>

        {/* No active exercise - show start or completed */}
        {!currentExercise && (
          <div className="space-y-6">
            {/* Intro Card */}
            <Card className="card-base bg-gradient-to-r from-purple-50 to-pink-50">
              <CardContent className="p-6">
                <h2 className="font-heading text-xl text-forest mb-3">What is a Truth Reset?</h2>
                <p className="text-gray-600 mb-4">
                  We all carry beliefs about ourselves that were given to us by others or formed during difficult moments. 
                  These beliefs feel like truth, but they're actually just stories we've accepted.
                </p>
                <p className="text-gray-600 mb-6">
                  This 5-step guided exercise will help you identify a limiting belief, trace its origin, 
                  examine the evidence, and replace it with an empowering truth you can carry forward.
                </p>
                
                <div className="flex items-center gap-4 p-4 bg-white/60 rounded-lg mb-6">
                  <Trophy className="h-8 w-8 text-amber-500" />
                  <div>
                    <p className="font-medium text-forest">Earn up to 130 diamonds</p>
                    <p className="text-sm text-gray-500">Points awarded at each step + completion bonus</p>
                  </div>
                </div>
                
                <Button 
                  onClick={startExercise}
                  disabled={saving}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="start-exercise-btn"
                >
                  {saving ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Starting...</>
                  ) : (
                    <><Sparkles className="h-4 w-4 mr-2" />Begin My Truth Reset</>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Completed Exercises Summary */}
            {progress?.completed_exercises?.length > 0 && (
              <Card className="card-base">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-heading text-forest flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      Your Transformations ({progress.total_completed})
                    </CardTitle>
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      +{progress.total_points_earned} diamonds earned
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {progress.completed_exercises.map((ex, index) => (
                    <div 
                      key={ex.id} 
                      className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100 hover:shadow-md transition-all cursor-pointer"
                      onClick={() => setSelectedExercise(ex)}
                      data-testid={`transformation-${index}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          {/* Date and Points */}
                          <div className="flex items-center gap-2 mb-3">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="text-xs text-gray-500">
                              {new Date(ex.completed_at).toLocaleDateString('en-US', {
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </span>
                            <Badge className="bg-purple-100 text-purple-700 text-xs">
                              +{ex.points_earned} diamonds
                            </Badge>
                          </div>
                          
                          {/* Transformation */}
                          <div className="space-y-3">
                            {/* Old Belief - struck through */}
                            <div className="flex items-start gap-2">
                              <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
                                <span className="text-red-500 text-xs">✕</span>
                              </div>
                              <div>
                                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Old Belief</p>
                                <p className="text-sm text-gray-500 line-through italic">
                                  "{ex.limiting_belief || 'Not recorded'}"
                                </p>
                              </div>
                            </div>
                            
                            {/* New Truth */}
                            <div className="flex items-start gap-2">
                              <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-0.5">
                                <Star className="h-3 w-3 text-green-600" />
                              </div>
                              <div>
                                <p className="text-xs text-green-600 uppercase tracking-wide mb-0.5">New Truth</p>
                                <p className="text-sm text-forest font-medium">
                                  "{ex.new_truth || 'Not recorded'}"
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Affirmation Card */}
                          {ex.affirmation && (
                            <div className="mt-4 p-3 bg-white/70 rounded-lg border border-amber-200">
                              <div className="flex items-start gap-2">
                                <Quote className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-xs text-amber-600 font-medium uppercase tracking-wide mb-1">Your Affirmation</p>
                                  <p className="text-sm text-forest italic font-medium">
                                    "{ex.affirmation}"
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* View Details Arrow */}
                        <div className="flex items-center">
                          <Button variant="ghost" size="sm" className="text-purple-600 hover:text-purple-700 p-2">
                            <Eye className="h-4 w-4 mr-1" /> View
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Active Exercise */}
        {currentExercise && currentStep && (
          <div className="space-y-6">
            {/* Progress */}
            <div className="flex items-center gap-4">
              <Progress 
                value={(currentExercise.current_step / steps.length) * 100} 
                className="flex-1 h-2"
              />
              <span className="text-sm text-gray-600">
                Step {currentExercise.current_step} of {steps.length}
              </span>
            </div>

            {/* Current Step Card */}
            <Card className="card-base">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
                    <StepIcon className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <Badge className="mb-2 bg-purple-100 text-purple-700">
                      Step {currentStep.step}
                    </Badge>
                    <CardTitle className="text-xl font-heading text-forest">
                      {currentStep.title}
                    </CardTitle>
                    <p className="text-terracotta font-medium mt-1">{currentStep.subtitle}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-gray-600">{currentStep.description}</p>
                
                {/* Main Prompt */}
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-forest font-medium">{currentStep.prompt}</p>
                </div>

                {/* Examples if available */}
                {currentStep.examples && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500">Examples:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {currentStep.examples.map((ex, i) => (
                        <li key={ex} className="text-sm text-gray-600 italic">"{ex}"</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Response Input(s) */}
                {currentStep.sub_prompts ? (
                  // Multiple inputs for Step 3
                  <div className="space-y-4">
                    {currentStep.sub_prompts.map((sub, i) => (
                      <div key={sub.label}>
                        <label className="text-sm font-medium text-forest block mb-2">
                          {sub.label}
                        </label>
                        <Textarea
                          placeholder={sub.prompt}
                          value={response[currentExercise.current_step]?.[`sub_${i}`] || ''}
                          onChange={(e) => setResponse({
                            ...response,
                            [currentExercise.current_step]: {
                              ...(response[currentExercise.current_step] || {}),
                              [`sub_${i}`]: e.target.value
                            }
                          })}
                          rows={3}
                          spellCheck={true}
                        />
                      </div>
                    ))}
                  </div>
                ) : currentStep.action_prompts ? (
                  // Multiple action items for Step 5
                  <div className="space-y-4">
                    {currentStep.action_prompts.map((action, i) => (
                      <div key={action.label}>
                        <label className="text-sm font-medium text-forest block mb-2">
                          {action.label}
                        </label>
                        <Textarea
                          placeholder={action.prompt}
                          value={response[currentExercise.current_step]?.[`action_${i}`] || ''}
                          onChange={(e) => setResponse({
                            ...response,
                            [currentExercise.current_step]: {
                              ...(response[currentExercise.current_step] || {}),
                              [`action_${i}`]: e.target.value
                            }
                          })}
                          rows={2}
                          spellCheck={true}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  // Single input with Voice support
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Write your response here..."
                      value={(() => {
                        const step = currentExercise.current_step;
                        const stepResp = response[step];
                        if (step === 1) {
                          return stepResp?.belief || (typeof stepResp === 'string' ? stepResp : '') || '';
                        } else if (step === 4) {
                          return stepResp?.new_belief || (typeof stepResp === 'string' ? stepResp : '') || '';
                        } else {
                          // For step 2 and 3, handle both object and string formats
                          if (typeof stepResp === 'object' && stepResp !== null) {
                            return stepResp.origin || stepResp.text || Object.values(stepResp)[0] || '';
                          }
                          return stepResp || '';
                        }
                      })()}
                      onChange={(e) => {
                        const val = e.target.value;
                        const step = currentExercise.current_step;
                        if (step === 1) {
                          setResponse({ ...response, 1: { belief: val } });
                        } else if (step === 4) {
                          setResponse({ ...response, 4: { new_belief: val } });
                        } else if (step === 2) {
                          setResponse({ ...response, 2: { origin: val } });
                        } else {
                          setResponse({ ...response, [step]: val });
                        }
                      }}
                      rows={5}
                      spellCheck={true}
                      data-testid="truth-reset-textarea"
                    />
                    <VoiceInput 
                      onTranscript={(text) => {
                        const step = currentExercise.current_step;
                        const currentVal = (() => {
                          const stepResp = response[step];
                          if (step === 1) return stepResp?.belief || '';
                          if (step === 4) return stepResp?.new_belief || '';
                          if (step === 2) return stepResp?.origin || '';
                          return typeof stepResp === 'string' ? stepResp : '';
                        })();
                        const newVal = currentVal ? `${currentVal}\n\n${text}` : text;
                        
                        if (step === 1) {
                          setResponse({ ...response, 1: { belief: newVal } });
                        } else if (step === 4) {
                          setResponse({ ...response, 4: { new_belief: newVal } });
                        } else if (step === 2) {
                          setResponse({ ...response, 2: { origin: newVal } });
                        } else {
                          setResponse({ ...response, [step]: newVal });
                        }
                      }}
                    />
                  </div>
                )}

                {/* Guidelines if available */}
                {currentStep.guidelines && (
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-sm font-medium text-amber-800 mb-2">Tips for your new truth:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {currentStep.guidelines.map((g, i) => (
                        <li key={g} className="text-sm text-amber-700">{g}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <Button 
                    variant="ghost" 
                    onClick={discardExercise}
                    className="text-gray-500"
                  >
                    Discard Exercise
                  </Button>
                  <Button
                    onClick={saveStep}
                    disabled={saving}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {saving ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                    ) : currentExercise.current_step === steps.length ? (
                      <><CheckCircle className="h-4 w-4 mr-2" />Complete Exercise</>
                    ) : (
                      <><ArrowRight className="h-4 w-4 mr-2" />Continue</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Exercise Detail Dialog */}
        <Dialog open={!!selectedExercise} onOpenChange={() => setSelectedExercise(null)}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
            {selectedExercise && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <RefreshCw className="h-5 w-5 text-purple-600" />
                    Truth Reset Summary
                  </DialogTitle>
                </DialogHeader>
                
                <div className="py-4 space-y-6">
                  {/* Date & Points */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar className="h-4 w-4" />
                      {new Date(selectedExercise.completed_at).toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                    <Badge className="bg-amber-100 text-amber-700">
                      +{selectedExercise.points_earned} diamonds
                    </Badge>
                  </div>

                  {/* The Transformation */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-5 space-y-4">
                    <h3 className="font-heading text-lg text-forest text-center mb-4">Your Transformation</h3>
                    
                    {/* Old Belief */}
                    <div className="bg-white/60 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-6 w-6 rounded-full bg-red-100 flex items-center justify-center">
                          <span className="text-red-500 text-sm">✕</span>
                        </div>
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          The Lie You Released
                        </span>
                      </div>
                      <p className="text-gray-500 line-through italic pl-8">
                        "{selectedExercise.limiting_belief || 'Not recorded'}"
                      </p>
                    </div>
                    
                    {/* Arrow Down */}
                    <div className="flex justify-center">
                      <div className="h-8 w-8 rounded-full bg-purple-200 flex items-center justify-center">
                        <ArrowRight className="h-4 w-4 text-purple-600 rotate-90" />
                      </div>
                    </div>
                    
                    {/* New Truth */}
                    <div className="bg-white/80 rounded-lg p-4 border-2 border-green-200">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center">
                          <Star className="h-3 w-3 text-green-600" />
                        </div>
                        <span className="text-xs font-semibold text-green-600 uppercase tracking-wide">
                          Your New Truth
                        </span>
                      </div>
                      <p className="text-forest font-semibold pl-8 text-lg">
                        "{selectedExercise.new_truth || 'Not recorded'}"
                      </p>
                    </div>
                  </div>
                  
                  {/* Affirmation */}
                  {selectedExercise.affirmation && (
                    <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                          <Quote className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">
                            Daily Affirmation
                          </p>
                          <p className="text-forest font-medium text-lg italic">
                            "{selectedExercise.affirmation}"
                          </p>
                          <p className="text-xs text-amber-600 mt-2">
                            Speak this truth over yourself daily 💛
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Commitments (if available) */}
                  {(selectedExercise.daily_reminder || selectedExercise.weekly_action || selectedExercise.accountability_partner) && (
                    <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                      <h4 className="font-medium text-forest text-sm uppercase tracking-wide">Your Commitments</h4>
                      
                      {selectedExercise.daily_reminder && (
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-500">Daily Reminder:</p>
                            <p className="text-sm text-gray-700">{selectedExercise.daily_reminder}</p>
                          </div>
                        </div>
                      )}
                      
                      {selectedExercise.weekly_action && (
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-500">Weekly Action:</p>
                            <p className="text-sm text-gray-700">{selectedExercise.weekly_action}</p>
                          </div>
                        </div>
                      )}
                      
                      {selectedExercise.accountability_partner && (
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-500">Accountability:</p>
                            <p className="text-sm text-gray-700">{selectedExercise.accountability_partner}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TruthResetPage;
